
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Search, User, Briefcase, Building2, HandshakeIcon, Contact, Check, X } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

interface RelationPickerProps {
	selectedRelations: {
		candidates: string[];
		offers: string[];
		companies: string[];
		matches: string[];
		contacts: string[];
	};
	onSelect: (type: string, id: string) => void;
	onRemove: (type: string, id: string) => void;
}

interface EntityItem {
	id: string;
	name: string;
	title?: string;
	secondary?: string;
	company_id?: string;
	company?: { id: string; name: string };
	candidate?: { id: string; name: string };
	job?: { id: string; title: string; company_id?: string };
}

export const RelationPicker = ({ selectedRelations, onSelect, onRemove }: RelationPickerProps) => {
	const { t } = useTranslation();
	const [searchTerm, setSearchTerm] = useState("");
	const [activeTab, setActiveTab] = useState("candidates");
	
	const [candidates, setCandidates] = useState<EntityItem[]>([]);
	const [offers, setOffers] = useState<EntityItem[]>([]);
	const [companies, setCompanies] = useState<EntityItem[]>([]);
	const [matches, setMatches] = useState<EntityItem[]>([]);
	const [contacts, setContacts] = useState<EntityItem[]>([]);
	
	const [selectedItems, setSelectedItems] = useState<{
		candidates: EntityItem[];
		offers: EntityItem[];
		companies: EntityItem[];
		matches: EntityItem[];
		contacts: EntityItem[];
	}>({
		candidates: [],
		offers: [],
		companies: [],
		matches: [],
		contacts: []
	});
	
	const [loading, setLoading] = useState<{[key: string]: boolean}>({
		candidates: false,
		offers: false,
		companies: false,
		matches: false,
		contacts: false
	});
	
	const [hasSearched, setHasSearched] = useState(false);

	// Load selected relations data on initial render
	useEffect(() => {
		if (Object.values(selectedRelations).some(arr => arr.length > 0)) {
			loadSelectedRelationsData();
		}
	}, []);

	// Fetch data based on active tab for suggested items or search
	useEffect(() => {
		if (!activeTab) return;
		
		if (searchTerm.trim()) {
			fetchData(activeTab, true);
			setHasSearched(true);
		} else if (!hasSearched) {
			// Only fetch suggestions if we're not immediately after a search
			fetchSuggestions(activeTab);
		} else {
			// Clear search results when search term is cleared
			clearResults(activeTab);
			setHasSearched(false);
		}
	}, [activeTab, searchTerm]);

	const clearResults = (entityType: string) => {
		switch (entityType) {
			case "candidates": setCandidates([]); break;
			case "offers": setOffers([]); break;
			case "companies": setCompanies([]); break;
			case "matches": setMatches([]); break;
			case "contacts": setContacts([]); break;
		}
	};

	const loadSelectedRelationsData = async () => {
		// Load data for each type that has selected items
		if (selectedRelations.candidates.length > 0) {
			await loadSelectedEntities("candidates", selectedRelations.candidates);
		}
		if (selectedRelations.offers.length > 0) {
			await loadSelectedEntities("offers", selectedRelations.offers);
		}
		if (selectedRelations.companies.length > 0) {
			await loadSelectedEntities("companies", selectedRelations.companies);
		}
		if (selectedRelations.matches.length > 0) {
			await loadSelectedEntities("matches", selectedRelations.matches);
		}
		if (selectedRelations.contacts.length > 0) {
			await loadSelectedEntities("contacts", selectedRelations.contacts);
		}
	};

	const loadSelectedEntities = async (entityType: string, ids: string[]) => {
		if (ids.length === 0) return;
		
		setLoading(prev => ({ ...prev, [entityType]: true }));
		
		try {
			let items: EntityItem[] = [];
			
			switch (entityType) {
				case "candidates":
					const { data: candidatesData } = await supabase
						.from('candidates')
						.select('id, name, email')
						.in('id', ids);
					
					items = candidatesData?.map(c => ({
						id: c.id,
						name: c.name,
						secondary: c.email || undefined
					})) || [];
					setSelectedItems(prev => ({ ...prev, candidates: items }));
					break;
					
				case "offers":
					const { data: offersData } = await supabase
						.from('jobs')
						.select('id, title, company_id, company:company_id (id, name)')
						.in('id', ids);
					
					items = offersData?.map(job => ({
						id: job.id,
						name: job.title,
						company_id: job.company_id,
						company: job.company ? { id: job.company.id, name: job.company.name } : undefined,
						secondary: job.company?.name || undefined
					})) || [];
					setSelectedItems(prev => ({ ...prev, offers: items }));
					break;
					
				case "companies":
					const { data: companiesData } = await supabase
						.from('companies')
						.select('id, name')
						.in('id', ids);
					
					items = companiesData?.map(c => ({
						id: c.id,
						name: c.name
					})) || [];
					setSelectedItems(prev => ({ ...prev, companies: items }));
					break;
					
				case "matches":
					const { data: matchesData } = await supabase
						.from('matches')
						.select('id, job_id, candidate_id, job:job_id (id, title, company_id), candidate:candidate_id (id, name)')
						.in('id', ids);
					
					items = matchesData?.map(m => ({
						id: m.id,
						name: m.candidate && m.candidate.name ? 
							`${m.candidate.name} - ${m.job && m.job.title ? m.job.title : ''}` : 
							(m.job && m.job.title ? m.job.title : 'Match'),
						candidate: m.candidate ? { id: m.candidate.id, name: m.candidate.name } : undefined,
						job: m.job ? { id: m.job.id, title: m.job.title, company_id: m.job.company_id } : undefined
					})) || [];
					setSelectedItems(prev => ({ ...prev, matches: items }));
					break;
					
				case "contacts":
					const { data: contactsData } = await supabase
						.from('contacts')
						.select('id, name, function, company_id, company:company_id (id, name)')
						.in('id', ids);
					
					items = contactsData?.map(c => ({
						id: c.id,
						name: c.name,
						company: c.company ? { id: c.company.id, name: c.company.name } : undefined,
						secondary: c.company?.name || c.function || undefined
					})) || [];
					setSelectedItems(prev => ({ ...prev, contacts: items }));
					break;
			}
		} catch (error) {
			console.error(`Error loading selected ${entityType}:`, error);
		} finally {
			setLoading(prev => ({ ...prev, [entityType]: false }));
		}
	};

	const fetchSuggestions = async (entityType: string) => {
		setLoading(prev => ({ ...prev, [entityType]: true }));
		
		try {
			let data: EntityItem[] = [];
			
			switch (entityType) {
				case "candidates":
					// Show candidates related to selected companies, offers, or matches
					if (selectedRelations.companies.length > 0 || 
						selectedRelations.offers.length > 0 || 
						selectedRelations.matches.length > 0) {
						
						// From matches
						if (selectedRelations.matches.length > 0) {
							const { data: candidatesFromMatches } = await supabase
								.from('matches')
								.select('candidate:candidate_id (id, name, email)')
								.in('id', selectedRelations.matches)
								.not('candidate_id', 'is', null);
								
							const uniqueCandidates = candidatesFromMatches?.map(item => ({
								id: item.candidate.id,
								name: item.candidate.name,
								secondary: item.candidate.email || undefined
							})) || [];
							
							data = [...data, ...uniqueCandidates];
						}
						
						// From job offers (show candidates with matching skills)
						if (selectedRelations.offers.length > 0) {
							const { data: jobsWithSkills } = await supabase
								.from('jobs')
								.select('skills')
								.in('id', selectedRelations.offers);
								
							const allSkills = jobsWithSkills?.flatMap(job => job.skills || []) || [];
							
							if (allSkills.length > 0) {
								const { data: candidatesWithSkills } = await supabase
									.from('candidates')
									.select('id, name, email')
									.overlaps('skills', allSkills)
									.limit(5);
									
								const uniqueCandidatesFromSkills = candidatesWithSkills?.map(c => ({
									id: c.id,
									name: c.name,
									secondary: c.email || undefined
								})) || [];
								
								// Add only unique candidates
								uniqueCandidatesFromSkills.forEach(candidate => {
									if (!data.some(c => c.id === candidate.id)) {
										data.push(candidate);
									}
								});
							}
						}
					} else {
						// Default: recent candidates
						const { data: recentCandidates } = await supabase
							.from('candidates')
							.select('id, name, email')
							.order('created_at', { ascending: false })
							.limit(5);
							
						data = recentCandidates?.map(c => ({
							id: c.id,
							name: c.name,
							secondary: c.email || undefined
						})) || [];
					}
					
					setCandidates(data);
					break;
					
				case "offers":
					// Show offers related to selected companies or candidates
					if (selectedRelations.companies.length > 0 || 
						selectedRelations.candidates.length > 0 ||
						selectedRelations.matches.length > 0) {
						
						// From companies
						if (selectedRelations.companies.length > 0) {
							const { data: offersFromCompanies } = await supabase
								.from('jobs')
								.select('id, title, company:company_id (name)')
								.in('company_id', selectedRelations.companies)
								.limit(5);
								
							data = offersFromCompanies?.map(job => ({
								id: job.id,
								name: job.title,
								secondary: job.company?.name || undefined
							})) || [];
						}
						
						// From matches
						if (selectedRelations.matches.length > 0) {
							const { data: offersFromMatches } = await supabase
								.from('matches')
								.select('job:job_id (id, title, company:company_id(name))')
								.in('id', selectedRelations.matches)
								.not('job_id', 'is', null);
								
							const uniqueOffers = offersFromMatches?.map(item => ({
								id: item.job.id,
								name: item.job.title,
								secondary: item.job.company?.name || undefined
							})) || [];
							
							// Add only unique offers
							uniqueOffers.forEach(offer => {
								if (!data.some(o => o.id === offer.id)) {
									data.push(offer);
								}
							});
						}
					} else {
						// Default: recent job offers
						const { data: recentOffers } = await supabase
							.from('jobs')
							.select('id, title, company:company_id (name)')
							.order('created_at', { ascending: false })
							.limit(5);
							
						data = recentOffers?.map(job => ({
							id: job.id,
							name: job.title,
							secondary: job.company?.name || undefined
						})) || [];
					}
					
					setOffers(data);
					break;
					
				case "companies":
					// Show companies related to selected offers, contacts or matches
					if (selectedRelations.offers.length > 0 || 
						selectedRelations.contacts.length > 0 ||
						selectedRelations.matches.length > 0) {
						
						// From offers
						if (selectedRelations.offers.length > 0) {
							const { data: companiesFromOffers } = await supabase
								.from('jobs')
								.select('company:company_id (id, name)')
								.in('id', selectedRelations.offers)
								.not('company_id', 'is', null);
								
							data = companiesFromOffers?.filter(item => item.company).map(item => ({
								id: item.company.id,
								name: item.company.name
							})) || [];
						}
						
						// From contacts
						if (selectedRelations.contacts.length > 0) {
							const { data: companiesFromContacts } = await supabase
								.from('contacts')
								.select('company:company_id (id, name)')
								.in('id', selectedRelations.contacts)
								.not('company_id', 'is', null);
								
							const uniqueCompanies = companiesFromContacts?.filter(item => item.company).map(item => ({
								id: item.company.id,
								name: item.company.name
							})) || [];
							
							// Add only unique companies
							uniqueCompanies.forEach(company => {
								if (!data.some(c => c.id === company.id)) {
									data.push(company);
								}
							});
						}
						
						// From matches (via job offers)
						if (selectedRelations.matches.length > 0) {
							const { data: companiesFromMatches } = await supabase
								.from('matches')
								.select('job:job_id (company:company_id (id, name))')
								.in('id', selectedRelations.matches)
								.not('job_id', 'is', null);
								
							const uniqueCompaniesFromMatches = companiesFromMatches
								?.filter(item => item.job && item.job.company)
								.map(item => ({
									id: item.job.company.id,
									name: item.job.company.name
								})) || [];
								
							// Add only unique companies
							uniqueCompaniesFromMatches.forEach(company => {
								if (!data.some(c => c.id === company.id)) {
									data.push(company);
								}
							});
						}
					} else {
						// Default: recent companies
						const { data: recentCompanies } = await supabase
							.from('companies')
							.select('id, name')
							.order('created_at', { ascending: false })
							.limit(5);
							
						data = recentCompanies?.map(c => ({
							id: c.id,
							name: c.name
						})) || [];
					}
					
					setCompanies(data);
					break;
					
				case "matches":
					// Show matches related to selected candidates, offers, or companies
					if (selectedRelations.candidates.length > 0 || 
						selectedRelations.offers.length > 0 || 
						selectedRelations.companies.length > 0) {
						
						// From candidates
						if (selectedRelations.candidates.length > 0) {
							const { data: matchesFromCandidates } = await supabase
								.from('matches')
								.select('id, job:job_id (id, title), candidate:candidate_id (id, name)')
								.in('candidate_id', selectedRelations.candidates)
								.limit(5);
								
							data = matchesFromCandidates?.map(m => ({
								id: m.id,
								name: m.candidate?.name ? `${m.candidate.name} - ${m.job?.title || ''}` : m.job?.title || 'Match',
								candidate: m.candidate ? { id: m.candidate.id, name: m.candidate.name } : undefined,
								job: m.job ? { id: m.job.id, title: m.job.title } : undefined
							})) || [];
						}
						
						// From job offers
						if (selectedRelations.offers.length > 0) {
							const { data: matchesFromOffers } = await supabase
								.from('matches')
								.select('id, job:job_id (id, title), candidate:candidate_id (id, name)')
								.in('job_id', selectedRelations.offers)
								.limit(5);
								
							const uniqueMatches = matchesFromOffers?.map(m => ({
								id: m.id,
								name: m.candidate?.name ? `${m.candidate.name} - ${m.job?.title || ''}` : m.job?.title || 'Match',
								candidate: m.candidate ? { id: m.candidate.id, name: m.candidate.name } : undefined,
								job: m.job ? { id: m.job.id, title: m.job.title } : undefined
							})) || [];
							
							// Add only unique matches
							uniqueMatches.forEach(match => {
								if (!data.some(m => m.id === match.id)) {
									data.push(match);
								}
							});
						}
						
						// From companies (via job offers)
						if (selectedRelations.companies.length > 0) {
							const { data: jobsFromCompanies } = await supabase
								.from('jobs')
								.select('id')
								.in('company_id', selectedRelations.companies);
								
							const jobIds = jobsFromCompanies?.map(job => job.id) || [];
							
							if (jobIds.length > 0) {
								const { data: matchesFromCompanies } = await supabase
									.from('matches')
									.select('id, job:job_id (id, title), candidate:candidate_id (id, name)')
									.in('job_id', jobIds)
									.limit(5);
									
								const uniqueMatches = matchesFromCompanies?.map(m => ({
									id: m.id,
									name: m.candidate?.name ? `${m.candidate.name} - ${m.job?.title || ''}` : m.job?.title || 'Match',
									candidate: m.candidate ? { id: m.candidate.id, name: m.candidate.name } : undefined,
									job: m.job ? { id: m.job.id, title: m.job.title } : undefined
								})) || [];
								
								// Add only unique matches
								uniqueMatches.forEach(match => {
									if (!data.some(m => m.id === match.id)) {
										data.push(match);
									}
								});
							}
						}
					} else {
						// Default: recent matches
						const { data: recentMatches } = await supabase
							.from('matches')
							.select('id, job:job_id (id, title), candidate:candidate_id (id, name)')
							.order('created_at', { ascending: false })
							.limit(5);
							
						data = recentMatches?.map(m => ({
							id: m.id,
							name: m.candidate?.name ? `${m.candidate.name} - ${m.job?.title || ''}` : m.job?.title || 'Match',
							candidate: m.candidate ? { id: m.candidate.id, name: m.candidate.name } : undefined,
							job: m.job ? { id: m.job.id, title: m.job.title } : undefined
						})) || [];
					}
					
					setMatches(data);
					break;
					
				case "contacts":
					// Show contacts related to selected companies
					if (selectedRelations.companies.length > 0) {
						const { data: contactsFromCompanies } = await supabase
							.from('contacts')
							.select('id, name, function, company:company_id (id, name)')
							.in('company_id', selectedRelations.companies)
							.limit(5);
							
						data = contactsFromCompanies?.map(c => ({
							id: c.id,
							name: c.name,
							company: c.company ? { id: c.company.id, name: c.company.name } : undefined,
							secondary: c.company?.name || c.function || undefined
						})) || [];
					} else {
						// Default: recent contacts
						const { data: recentContacts } = await supabase
							.from('contacts')
							.select('id, name, function, company:company_id (id, name)')
							.order('created_at', { ascending: false })
							.limit(5);
							
						data = recentContacts?.map(c => ({
							id: c.id,
							name: c.name,
							company: c.company ? { id: c.company.id, name: c.company.name } : undefined,
							secondary: c.company?.name || c.function || undefined
						})) || [];
					}
					
					setContacts(data);
					break;
			}
		} catch (error) {
			console.error(`Error fetching suggestions for ${entityType}:`, error);
		} finally {
			setLoading(prev => ({ ...prev, [entityType]: false }));
		}
	};

	const fetchData = async (entityType: string, isSearch: boolean = false) => {
		setLoading(prev => ({ ...prev, [entityType]: true }));
		
		try {
			let data: EntityItem[] = [];
			
			switch (entityType) {
				case "candidates":
					const { data: candidatesData } = await supabase
						.from('candidates')
						.select('id, name, email')
						.ilike('name', `%${searchTerm}%`)
						.limit(20);
					
					data = candidatesData?.map(c => ({
						id: c.id,
						name: c.name,
						secondary: c.email || undefined
					})) || [];
					setCandidates(data);
					break;
					
				case "offers":
					const { data: offersData } = await supabase
						.from('jobs')
						.select('id, title, company_id, company:company_id (id, name)')
						.ilike('title', `%${searchTerm}%`)
						.limit(20);
					
					data = offersData?.map(job => ({
						id: job.id,
						name: job.title,
						company_id: job.company_id,
						company: job.company ? { id: job.company.id, name: job.company.name } : undefined,
						secondary: job.company?.name || undefined
					})) || [];
					setOffers(data);
					break;
					
				case "companies":
					const { data: companiesData } = await supabase
						.from('companies')
						.select('id, name')
						.ilike('name', `%${searchTerm}%`)
						.limit(20);
					
					data = companiesData?.map(c => ({
						id: c.id,
						name: c.name
					})) || [];
					setCompanies(data);
					break;
					
				case "matches":
					const { data: matchesData } = await supabase
						.from('matches')
						.select('id, job_id, candidate_id, job:job_id (id, title, company_id), candidate:candidate_id (id, name)')
						.or(`job.title.ilike.%${searchTerm}%,candidate.name.ilike.%${searchTerm}%`)
						.limit(20);
					
					data = matchesData?.map(m => ({
						id: m.id,
						name: m.candidate && m.candidate.name ? 
							`${m.candidate.name} - ${m.job && m.job.title ? m.job.title : ''}` : 
							(m.job && m.job.title ? m.job.title : 'Match'),
						candidate: m.candidate ? { id: m.candidate.id, name: m.candidate.name } : undefined,
						job: m.job ? { id: m.job.id, title: m.job.title, company_id: m.job.company_id } : undefined
					})) || [];
					setMatches(data);
					break;
					
				case "contacts":
					const { data: contactsData } = await supabase
						.from('contacts')
						.select('id, name, function, company_id, company:company_id (id, name)')
						.ilike('name', `%${searchTerm}%`)
						.limit(20);
					
					data = contactsData?.map(c => ({
						id: c.id,
						name: c.name,
						company: c.company ? { id: c.company.id, name: c.company.name } : undefined,
						secondary: c.company?.name || c.function || undefined
					})) || [];
					setContacts(data);
					break;
			}

		} catch (error) {
			console.error(`Error fetching ${entityType}:`, error);
		} finally {
			setLoading(prev => ({ ...prev, [entityType]: false }));
		}
	};

	const handleTabChange = (tabValue: string) => {
		setActiveTab(tabValue);
		setSearchTerm("");
		setHasSearched(false);
	};
	
	const handleEntitySelect = async (type: string, entity: EntityItem) => {
		// First add this entity
		onSelect(type, entity.id);
		
		// Auto-linking logic
		if (type === 'matches') {
			// When linking a match, also link the candidate, job offer and company
			if (entity.candidate?.id) {
				onSelect('candidates', entity.candidate.id);
			}
			
			if (entity.job?.id) {
				onSelect('offers', entity.job.id);
				
				// Get company from job offer
				if (entity.job.company_id) {
					try {
						const { data: jobData } = await supabase
							.from('jobs')
							.select('company_id')
							.eq('id', entity.job.id)
							.single();
							
						if (jobData?.company_id) {
							onSelect('companies', jobData.company_id);
						}
					} catch (err) {
						console.error("Error fetching company for job:", err);
					}
				}
			}
		} else if (type === 'offers' && entity.company_id) {
			// When linking a job offer, also link the company
			onSelect('companies', entity.company_id);
		} else if (type === 'contacts' && entity.company?.id) {
			// When linking a contact, also link the company
			onSelect('companies', entity.company.id);
		}
		
		// Update selected items
		setSelectedItems(prev => ({
			...prev,
			[type]: [...prev[type], entity]
		}));
	};
	
	const getEntityIcon = (entityType: string) => {
		switch (entityType) {
			case "candidates": return <User className="h-4 w-4" />;
			case "offers": return <Briefcase className="h-4 w-4" />;
			case "companies": return <Building2 className="h-4 w-4" />;
			case "matches": return <HandshakeIcon className="h-4 w-4" />;
			case "contacts": return <Contact className="h-4 w-4" />;
			default: return null;
		}
	};

	// Filter out items that are already selected
	const getFilteredResults = (type: string, items: EntityItem[]) => {
		const selectedIds = selectedRelations[type as keyof typeof selectedRelations];
		return items.filter(item => !selectedIds.includes(item.id));
	};

	return (
		<div className="h-full flex flex-col">
			<Tabs 
				defaultValue="candidates" 
				value={activeTab} 
				onValueChange={handleTabChange}
				className="w-full"
			>
				<TabsList className="w-full grid grid-cols-5">
					<TabsTrigger value="candidates" title={t("candidates.title")}>
						<User className="h-4 w-4" />
					</TabsTrigger>
					<TabsTrigger value="offers" title={t("jobs.title")}>
						<Briefcase className="h-4 w-4" />
					</TabsTrigger>
					<TabsTrigger value="companies" title={t("companies.title")}>
						<Building2 className="h-4 w-4" />
					</TabsTrigger>
					<TabsTrigger value="matches" title={t("matches.title")}>
						<HandshakeIcon className="h-4 w-4" />
					</TabsTrigger>
					<TabsTrigger value="contacts" title={t("contacts.title")}>
						<Contact className="h-4 w-4" />
					</TabsTrigger>
				</TabsList>

				{/* Selected items section */}
				{selectedRelations[activeTab as keyof typeof selectedRelations].length > 0 && (
					<div className="my-3 border rounded-md p-2 bg-gray-50 max-h-[150px] overflow-y-auto">
						<h4 className="text-sm font-medium mb-2 text-gray-500">Selected {activeTab}</h4>
						{selectedItems[activeTab as keyof typeof selectedItems].map((item) => (
							<div 
								key={item.id}
								className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-gray-100"
							>
								<div className="flex items-center">
									<div className="h-5 w-5 bg-green-500 rounded-full flex items-center justify-center mr-2">
										<Check className="h-3 w-3 text-white" />
									</div>
									<div className="text-sm">
										<div className="font-medium">{item.name}</div>
										{item.secondary && (
											<div className="text-xs text-gray-500">{item.secondary}</div>
										)}
									</div>
								</div>
								<button
									onClick={() => onRemove(activeTab, item.id)}
									className="h-6 w-6 rounded-full hover:bg-gray-200 flex items-center justify-center"
								>
									<X className="h-3.5 w-3.5 text-gray-600" />
								</button>
							</div>
						))}
					</div>
				)}

				<div className="relative my-2">
					<Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("common.search")}
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						className="pl-8"
					/>
				</div>

				<ScrollArea className="h-[260px] overflow-y-auto w-full rounded-md border">
					<div className="p-4">
						<TabsContent value="candidates" className="m-0">
							{loading.candidates ? (
								<div className="flex justify-center py-4">
									<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
								</div>
							) : searchTerm && getFilteredResults('candidates', candidates).length === 0 ? (
								<div className="text-center py-4 text-gray-500">
									{t("common.noResults")}
								</div>
							) : !searchTerm && getFilteredResults('candidates', candidates).length === 0 ? (
								<div className="text-center py-4 text-gray-500">
									{t("candidates.empty")}
								</div>
							) : (
								getFilteredResults('candidates', candidates).map((item) => (
									<Button
										key={item.id}
										variant="ghost"
										className="w-full justify-start mb-1"
										onClick={() => handleEntitySelect('candidates', item)}
									>
										<User className="h-4 w-4 mr-2" />
										<div className="text-left">
											<div>{item.name}</div>
											{item.secondary && <div className="text-xs text-gray-500">{item.secondary}</div>}
										</div>
									</Button>
								))
							)}
						</TabsContent>

						<TabsContent value="offers" className="m-0">
							{loading.offers ? (
								<div className="flex justify-center py-4">
									<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
								</div>
							) : searchTerm && getFilteredResults('offers', offers).length === 0 ? (
								<div className="text-center py-4 text-gray-500">
									{t("common.noResults")}
								</div>
							) : !searchTerm && getFilteredResults('offers', offers).length === 0 ? (
								<div className="text-center py-4 text-gray-500">
									{t("jobs.empty")}
								</div>
							) : (
								getFilteredResults('offers', offers).map((item) => (
									<Button
										key={item.id}
										variant="ghost"
										className="w-full justify-start mb-1"
										onClick={() => handleEntitySelect('offers', item)}
									>
										<Briefcase className="h-4 w-4 mr-2" />
										<div className="text-left">
											<div>{item.name}</div>
											{item.secondary && <div className="text-xs text-gray-500">{item.secondary}</div>}
										</div>
									</Button>
								))
							)}
						</TabsContent>

						<TabsContent value="companies" className="m-0">
							{loading.companies ? (
								<div className="flex justify-center py-4">
									<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
								</div>
							) : searchTerm && getFilteredResults('companies', companies).length === 0 ? (
								<div className="text-center py-4 text-gray-500">
									{t("common.noResults")}
								</div>
							) : !searchTerm && getFilteredResults('companies', companies).length === 0 ? (
								<div className="text-center py-4 text-gray-500">
									{t("companies.empty")}
								</div>
							) : (
								getFilteredResults('companies', companies).map((item) => (
									<Button
										key={item.id}
										variant="ghost"
										className="w-full justify-start mb-1"
										onClick={() => handleEntitySelect('companies', item)}
									>
										<Building2 className="h-4 w-4 mr-2" />
										{item.name}
									</Button>
								))
							)}
						</TabsContent>

						<TabsContent value="matches" className="m-0">
							{loading.matches ? (
								<div className="flex justify-center py-4">
									<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
								</div>
							) : searchTerm && getFilteredResults('matches', matches).length === 0 ? (
								<div className="text-center py-4 text-gray-500">
									{t("common.noResults")}
								</div>
							) : !searchTerm && getFilteredResults('matches', matches).length === 0 ? (
								<div className="text-center py-4 text-gray-500">
									{t("matches.empty")}
								</div>
							) : (
								getFilteredResults('matches', matches).map((item) => (
									<Button
										key={item.id}
										variant="ghost"
										className="w-full justify-start mb-1"
										onClick={() => handleEntitySelect('matches', item)}
									>
										<HandshakeIcon className="h-4 w-4 mr-2" />
										{item.name}
									</Button>
								))
							)}
						</TabsContent>

						<TabsContent value="contacts" className="m-0">
							{loading.contacts ? (
								<div className="flex justify-center py-4">
									<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
								</div>
							) : searchTerm && getFilteredResults('contacts', contacts).length === 0 ? (
								<div className="text-center py-4 text-gray-500">
									{t("common.noResults")}
								</div>
							) : !searchTerm && getFilteredResults('contacts', contacts).length === 0 ? (
								<div className="text-center py-4 text-gray-500">
									{t("contacts.empty")}
								</div>
							) : (
								getFilteredResults('contacts', contacts).map((item) => (
									<Button
										key={item.id}
										variant="ghost"
										className="w-full justify-start mb-1"
										onClick={() => handleEntitySelect('contacts', item)}
									>
										<Contact className="h-4 w-4 mr-2" />
										<div className="text-left">
											<div>{item.name}</div>
											{item.secondary && <div className="text-xs text-gray-500">{item.secondary}</div>}
										</div>
									</Button>
								))
							)}
						</TabsContent>
					</div>
				</ScrollArea>
			</Tabs>
		</div>
	);
};
